import { Alert, CircularProgress, Grid, Link, SxProps } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import {
  ExamResult,
  ExamResultQuestionData,
  ExamResultQuestionKind,
  ExamResultVerdict,
  StatusCodes,
} from "@knowfully/ui.common";
import useGlobalContext from "hooks/globalData";
import { Link as RouterLink, useParams } from "react-router-dom";
import { useQuery } from "react-query";
import { FormattedDate, FormattedMessage as T, useIntl } from "react-intl";
import {
  ApiException,
  UserExamResultQuestion,
} from "services/organizationPortalClient";
import { NotFoundPage } from "pages/InvalidStates/NotFoundPage";
import { examQuestionKind } from "helpers/examQuestionType";

const backLinkStyle: SxProps = {
  display: "inline-flex",
  alignItems: "center",
  gap: 0.5,
  mb: 2,
  fontWeight: 600,
};

const questionVerdict = (
  question: UserExamResultQuestion
): ExamResultVerdict | undefined => {
  if (!question.answers?.length) {
    return undefined;
  }

  const allCorrect = question.answers.every(
    (answer) => !!answer.isSelectedAnswer === !!answer.isAnswer
  );

  return allCorrect ? "correct" : "incorrect";
};

export default function ExamResultPage() {
  const { formatMessage: t } = useIntl();
  const { certificatesService, certificatesUrl } = useGlobalContext();
  const { completionId, userExamId } = useParams<{
    completionId: string;
    userExamId: string;
  }>();

  const { data, isLoading, isError, error } = useQuery(
    ["org-exam-result", completionId, userExamId],
    () => certificatesService.getExamResult(completionId!, userExamId!),
    { enabled: !!completionId && !!userExamId }
  );

  const result = data?.result;

  if (isLoading) {
    return (
      <Grid container justifyContent="center">
        <CircularProgress />
      </Grid>
    );
  }

  if (isError) {
    if ((error as ApiException)?.status === StatusCodes.NOT_FOUND) {
      return <NotFoundPage />;
    }

    return <Alert severity="error">{t({ id: "examResultFetchError" })}</Alert>;
  }

  if (!result) {
    return <NotFoundPage />;
  }

  const passed = result.examPassed === true;
  const attemptsTotal = result.attemptsAllowed ?? result.userAttemptsCount;
  let scoreLine: string | undefined;

  if (result.score != null) {
    scoreLine = passed
      ? t({ id: "examScorePassed" }, { score: result.score })
      : t(
          { id: "examScoreFailed" },
          { score: result.score, passingScore: result.passingScore }
        );
  }

  const questions: ExamResultQuestionData[] =
    result.questions?.map((question) => {
      const kind = examQuestionKind(question.questionTypeId);
      return {
        id: question.questionId,
        kind,
        number: question.questionNumber,
        text: question.question,
        verdict: questionVerdict(question),
        textAnswer:
          kind === ExamResultQuestionKind.Text
            ? question.answers?.[0]?.answerText ?? ""
            : undefined,
        questionFeedback: question.questionFeedback,
        answers: question.answers?.map((answer) => ({
          id: answer.id,
          content: answer.value ?? answer.answerText ?? "",
          isCorrect: answer.isAnswer === true,
          isSelected: !!answer.isSelectedAnswer,
          feedback: answer.feedback,
        })),
      };
    }) ?? [];

  return (
    <ExamResult
      title={result.examName}
      questionsCount={result.questionsCount}
      passed={passed}
      scoreLine={scoreLine}
      attemptValue={
        result.attemptNumber != null
          ? t(
              { id: "attemptProgress" },
              { current: result.attemptNumber, total: attemptsTotal }
            )
          : undefined
      }
      attemptDate={
        result.attemptDate ? <FormattedDate value={result.attemptDate} /> : undefined
      }
      learner={{
        firstName: data?.firstName,
        lastName: data?.lastName,
        email: data?.email,
      }}
      questions={questions}
      backLink={
        <Link component={RouterLink} to={certificatesUrl()} sx={backLinkStyle}>
          <ChevronLeftIcon fontSize="small" />
          <T id="backToCertificates" />
        </Link>
      }
    />
  );
}
